import { localStorageHelper, Messages } from 'utils';
import { IAction } from 'helpers/interfaces';
import { UserTypes } from 'redux/actions/user-actions';
import { errorHandler } from 'helpers/errorHandler';
import { GeneralConfigProviderTypes } from '../../actions/configProvider/general-actions';
import { addToPendingQueue, updatePendingQueue } from './userReducerHelper';
import i18next from 'i18next';
import dayjs from 'dayjs';
import { currencyNameConverter } from 'utils/Currency/currencyNameConverter';
import {
    canAcceptAuthenticationResult,
    SessionEvent,
    SessionStatus,
    transitionSession,
} from 'session/sessionState';
import { UserConfigurationStatus } from 'session/userConfigurationState';
import { emptyUserConfiguration, IUserConfigurationData } from 'session/userConfigurationBootstrap';
const PASSWORD_CHANGE_REQUIRED_CODE = 9;

export interface IUserState {
    name?: string;
    email?: string;
    role?: string;
    pendingQueue: string[];
    authorities: string[];
    sessionStatus: SessionStatus;
    userConfigurationStatus: UserConfigurationStatus;
    isLoading: boolean;
    searchString: string;
    shouldDisplayTestData: boolean;
    error?: string;
    newName?: string;
    isNameChanged?: boolean | null;
    isPasswordChanged?: boolean | null;
    isPreferencesChanged?: boolean | null;
    preferencesUpdateError?: string | null;
    passwordChangeRequired?: boolean;
    defaultPasswordChanged?: boolean;
    dateOfLastRefresh?: Date;
    games: any[];
    areGamesLoading: boolean;
    gameConfigurations: any[];
    areGameConfigurationsLoading: boolean;
    allCurrencies: any[];
    currenciesByBrand: any[];
    timezoneList: any[];
    countries: any[];
    allGames: any[];
}

const newSearchString = localStorageHelper.getChainedValue('user.search.currentSearchString') || '';
const defaultPreferences = {
    isNameChanged: null,
    isPasswordChanged: null,
    isPreferencesChanged: null,
    preferencesUpdateError: null,
};

export const user: IUserState = {
    name: '',
    email: '',
    role: '',
    authorities: [],
    pendingQueue: [],
    sessionStatus: SessionStatus.INITIALIZING,
    userConfigurationStatus: UserConfigurationStatus.IDLE,
    isLoading: false,
    shouldDisplayTestData: false,
    passwordChangeRequired: false,
    defaultPasswordChanged: false,
    searchString: newSearchString,
    games: [],
    areGamesLoading: false,
    gameConfigurations: [],
    areGameConfigurationsLoading: false,
    ...emptyUserConfiguration(),
    ...defaultPreferences,
};

const userReducer = (state: IUserState = { ...user }, actions: IAction) => {
    switch (actions.type) {
        case UserTypes.LOGIN_REQUEST: {
            return {
                ...state,
                error: '',
                isLoading: true,
                sessionStatus: transitionSession(state.sessionStatus, SessionEvent.LOGIN_STARTED),
                userConfigurationStatus: UserConfigurationStatus.IDLE,
                email: actions.payload.email,
            };
        }
        case UserTypes.INFO_REQUEST: {
            return {
                ...state,
                userConfigurationStatus:
                    state.userConfigurationStatus === UserConfigurationStatus.READY
                        ? UserConfigurationStatus.READY
                        : UserConfigurationStatus.LOADING,
            };
        }
        case UserTypes.SESSION_REQUEST: {
            return {
                ...state,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    SessionEvent.SESSION_CHECK_STARTED,
                ),
            };
        }
        case UserTypes.LOGOUT_REQUEST: {
            return {
                ...state,
                sessionStatus: transitionSession(state.sessionStatus, SessionEvent.LOGOUT_STARTED),
            };
        }
        case UserTypes.LOGIN_FAILED: {
            const error = actions?.payload?.response?.data?.error;

            if (error?.code === PASSWORD_CHANGE_REQUIRED_CODE) {
                return {
                    ...state,
                    isLoading: false,
                    passwordChangeRequired: true,
                    sessionStatus: transitionSession(
                        state.sessionStatus,
                        SessionEvent.AUTHENTICATION_FAILED,
                    ),
                };
            }

            return {
                ...state,
                isLoading: false,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    SessionEvent.AUTHENTICATION_FAILED,
                ),
            };
        }
        case UserTypes.LOGOUT:
        case UserTypes.SESSION_EXPIRED: {
            const locale =
                localStorageHelper.getChainedValue('user.preferences.locale') ||
                localStorageHelper.getChainedValue('locale') ||
                'en';

            localStorageHelper.remove('headers', 'timestamp', 'user', 'gamesList');
            localStorageHelper.set('locale', locale);

            return {
                ...user,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    actions.type === UserTypes.SESSION_EXPIRED
                        ? SessionEvent.SESSION_EXPIRED
                        : SessionEvent.LOGOUT_COMPLETED,
                ),
            };
        }

        case UserTypes.LOGIN_SUCCESS: {
            return state;
        }

        case UserTypes.LOGOUT_FAILED: {
            return {
                ...state,
                sessionStatus: transitionSession(state.sessionStatus, SessionEvent.LOGOUT_FAILED),
            };
        }

        case UserTypes.GET_ALL_GAMES_BY_USER_REQUEST: {
            return { ...state, areGamesLoading: true };
        }

        case UserTypes.GET_ALL_GAMES_BY_USER_SUCCESS: {
            return { ...state, areGamesLoading: false, games: actions.payload };
        }

        case UserTypes.GET_ALL_GAME_CONFIGURATIONS_BY_USER_REQUEST: {
            return { ...state, areGameConfigurationsLoading: true };
        }

        case UserTypes.GET_ALL_GAME_CONFIGURATIONS_BY_USER_SUCCESS: {
            return {
                ...state,
                areGameConfigurationsLoading: false,
                gameConfigurations: actions.payload,
            };
        }

        case UserTypes.GET_ALL_GAME_CONFIGURATIONS_BY_USER_FAILED: {
            return {
                ...state,
                areGameConfigurationsLoading: false,
            };
        }

        case UserTypes.DATE_OF_LAST_REFRESH_SUCCESS: {
            return { ...state, dateOfLastRefresh: actions.payload };
        }

        case UserTypes.CHANGE_DEFAULT_PASSWORD_REQUEST: {
            return { ...state, isLoading: true };
        }

        case UserTypes.CHANGE_DEFAULT_PASSWORD_SUCCESS: {
            Messages.success(i18next.t('default_password_successfully_changed'));

            return {
                ...state,
                isLoading: false,
                passwordChangeRequired: false,
                defaultPasswordChanged: true,
            };
        }

        case UserTypes.CHANGE_DEFAULT_PASSWORD_FAILED: {
            return {
                ...state,
                isLoading: false,
                passwordChangeRequired: false,
            };
        }

        case UserTypes.INFO_SUCCESS: {
            if (!canAcceptAuthenticationResult(state.sessionStatus)) {
                return state;
            }

            const {
                configuration = emptyUserConfiguration(),
                ...accountInfo
            }: { configuration?: IUserConfigurationData; [key: string]: any } = actions.payload;

            return {
                ...state,
                ...accountInfo,
                ...configuration,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    SessionEvent.AUTHENTICATION_CONFIRMED,
                ),
                userConfigurationStatus: UserConfigurationStatus.READY,
                isLoading: false,
            };
        }
        case UserTypes.SESSION_SUCCESS: {
            if (!canAcceptAuthenticationResult(state.sessionStatus)) {
                return state;
            }

            return {
                ...state,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    SessionEvent.AUTHENTICATION_CONFIRMED,
                ),
            };
        }

        case UserTypes.INFO_FAILED: {
            return {
                ...state,
                userConfigurationStatus:
                    state.userConfigurationStatus === UserConfigurationStatus.READY
                        ? UserConfigurationStatus.READY
                        : UserConfigurationStatus.FAILED,
                isLoading: false,
            };
        }
        case UserTypes.SESSION_FAILED: {
            const isBackgroundRevalidation = Boolean(actions.meta?.revalidation);

            return {
                ...state,
                sessionStatus: transitionSession(
                    state.sessionStatus,
                    isBackgroundRevalidation
                        ? SessionEvent.REVALIDATION_FAILED
                        : SessionEvent.AUTHENTICATION_FAILED,
                ),
                userConfigurationStatus: isBackgroundRevalidation
                    ? state.userConfigurationStatus
                    : UserConfigurationStatus.IDLE,
                isLoading: false,
            };
        }

        case UserTypes.UPDATE_PREFERENCES_REQUEST: {
            return { ...state, ...defaultPreferences };
        }

        case UserTypes.UPDATE_PREFERENCES_SUCCESS: {
            localStorageHelper.setChainedValue('user.preferences', actions.payload);

            return { ...state, isPreferencesChanged: true };
        }

        case UserTypes.UPDATE_PREFERENCES_FAILED: {
            const preferencesUpdateError = errorHandler.getErrorText(actions.payload);

            return {
                ...state,
                preferencesUpdateError,
                isPreferencesChanged: false,
            };
        }

        case UserTypes.CHANGE_NAME_REQUEST:
        case UserTypes.CHANGE_PASSWORD_REQUEST: {
            return {
                ...state,
                ...defaultPreferences,
                newName: actions.payload.name,
            };
        }

        case UserTypes.CHANGE_NAME_SUCCESS: {
            localStorageHelper.setChainedValue('user.name', state.newName);

            return { ...state, isNameChanged: true };
        }

        case UserTypes.CHANGE_NAME_FAILED: {
            const preferencesUpdateError = 'user_name_was_not_changed';

            return { ...state, preferencesUpdateError, isNameChanged: false };
        }

        case UserTypes.CHANGE_PASSWORD_SUCCESS: {
            return { ...state, isPasswordChanged: true };
        }

        case UserTypes.CHANGE_PASSWORD_FAILED: {
            // TODO: will get error from errorHandler when server finished with errors
            const preferencesUpdateError = 'change_password_failed';

            return {
                ...state,
                preferencesUpdateError,
                isPasswordChanged: false,
            };
        }

        case UserTypes.REMOVE_IS_ERROR_STATE: {
            return { ...state, error: '' };
        }

        case GeneralConfigProviderTypes.GET_DEFAULT_GAME_CONFIGS_REQUEST: {
            return addToPendingQueue(state, actions);
        }

        case UserTypes.GET_ALL_CURRENCIES_REQUEST: {
            return addToPendingQueue(state, actions);
        }

        case UserTypes.TIME_ZONE_REQUEST: {
            return addToPendingQueue(state, actions);
        }

        case UserTypes.GAMES_BY_OPERATOR_REQUEST: {
            return addToPendingQueue(state, actions);
        }

        case GeneralConfigProviderTypes.GET_DEFAULT_GAME_CONFIGS_SUCCESS: {
            return updatePendingQueue(state, actions);
        }

        case UserTypes.ALL_CURRENCY_SUCCESS: {
            const allCurrencies = actions.payload.response || [];

            localStorageHelper.setChainedValue('user.allCurrencies', allCurrencies);
            currencyNameConverter.setCurrencies(allCurrencies);

            return {
                ...updatePendingQueue(state, actions),
                allCurrencies,
            };
        }

        case UserTypes.GET_ALL_CURRENCIES_BY_BRAND_SUCCESS: {
            const currenciesByBrand = actions.payload.response || [];

            localStorageHelper.setChainedValue('user.currenciesByBrand', currenciesByBrand);

            return {
                ...updatePendingQueue(state, actions),
                currenciesByBrand,
            };
        }

        case GeneralConfigProviderTypes.GET_ALL_GAMES_SUCCESS: {
            return updatePendingQueue(state, actions);
        }

        case UserTypes.TIME_ZONE_SUCCESS: {
            localStorage.setItem('timestamp', dayjs().format());

            return {
                ...updatePendingQueue(state, actions),
                timezoneList: actions.payload.response || [],
            };
        }

        case UserTypes.GAMES_BY_OPERATOR_SUCCESS: {
            return updatePendingQueue(state, actions);
        }

        case UserTypes.GET_ALL_COUNTRIES_SUCCESS: {
            return {
                ...updatePendingQueue(state, actions),
                countries: actions.payload.response || [],
            };
        }

        case UserTypes.CHANGE_STATE_DISPLAYING_TEST_DATA: {
            localStorageHelper.setChainedValue('user.preferences.displayTestData', actions.payload);

            return { ...state, shouldDisplayTestData: actions.payload };
        }
        case UserTypes.SHOW_ERROR_NOTIFICATION: {
            Messages.error(actions.payload);

            return { ...state };
        }

        default:
            return state;
    }
};

export default userReducer;
